/**
 * Illustrated frames for the Corporation Resource Histogram video tour.
 * These mimic the live page (left legend, stacked shop load, one-line chrome).
 */

import React from 'react';
import { WindowsChrome } from './frames';

const FONT = '"Segoe UI", system-ui, -apple-system, sans-serif';
const ACCENT = '#0f766e';
const JOBS = [
  ['Atura 26LG0126', '#1d4ed8', 42],
  ['ACI 26FL1006', '#ea580c', 18],
  ['Airgas 25US1080', '#16a34a', 10],
  ['Goodyear 75998', '#9333ea', 22],
];

function Chip({ children, on, dark }) {
  return (
    <span style={{
      padding: '2px 7px',
      borderRadius: 5,
      fontSize: 9,
      fontWeight: 700,
      border: `1px solid ${on ? ACCENT : '#d1d5db'}`,
      background: dark ? ACCENT : on ? ACCENT : '#fff',
      color: dark || on ? '#fff' : '#334155',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

function FabChrome({ children, highlight = null }) {
  const glow = (key) => (highlight === key ? {
    boxShadow: `0 0 0 2px ${ACCENT}`,
    background: '#f0fdfa',
  } : {});
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', fontFamily: FONT, background: '#f8fafc' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap',
        padding: '6px 8px', background: '#fff', borderBottom: '1px solid #e2e8f0', overflow: 'hidden',
      }}>
        <div style={{ fontWeight: 800, fontSize: 11, color: '#0f172a', flexShrink: 0 }}>Corporation Resource Histogram</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, flexShrink: 0 }}>
          <Chip on={highlight === 'filters'}>Filters</Chip>
          <Chip>KPIs</Chip>
          <Chip on={highlight === 'source'}>Source</Chip>
          <Chip on={highlight === 'planning'}>Planning</Chip>
          <Chip>Workbooks (26)</Chip>
          <Chip>Period table</Chip>
          <Chip>Help</Chip>
          <Chip>Audit Log</Chip>
          <Chip dark={highlight === 'reload'} on={highlight === 'reload'}>Import</Chip>
          <Chip>Export</Chip>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: 8, ...glow(highlight) }}>
        {children}
      </div>
    </div>
  );
}

function MiniLegend({ spotlight }) {
  return (
    <div style={{ width: 118, flexShrink: 0, paddingRight: 6 }}>
      <div style={{ fontSize: 8, fontWeight: 800, color: '#64748b', letterSpacing: 0.3, marginBottom: 6 }}>
        LEGEND · MAN-WEEKS
      </div>
      {JOBS.map(([name, color]) => (
        <div key={name} style={{
          display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5,
          opacity: spotlight && spotlight !== name ? 0.35 : 1,
        }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 9, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name}
          </span>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: '#0f172a' }} />
        <span style={{ fontSize: 9, color: '#334155' }}>Shop capacity</span>
      </div>
    </div>
  );
}

function MiniChart({ overLine }) {
  const weeks = [
    [18, 8, 6, 4],
    [22, 10, 8, 12],
    [14, 6, 4, 8],
    [16, 7, 5, 6],
    [20, 9, 7, 10],
  ];
  const cap = 36;
  const max = 56;
  return (
    <div style={{ flex: 1, minWidth: 0, position: 'relative', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 8px 4px' }}>
      <div style={{ position: 'absolute', left: 6, top: 6, fontSize: 8, color: '#94a3b8', fontWeight: 700 }}>Man-weeks</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', height: '78%', gap: 10, padding: '18px 8px 0 28px' }}>
        {weeks.map((stack, i) => {
          const total = stack.reduce((s, n) => s + n, 0);
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', height: '100%', position: 'relative' }}>
              {stack.map((h, j) => (
                <div key={j} style={{
                  height: `${(h / max) * 100}%`,
                  background: JOBS[j][1],
                  borderRadius: j === stack.length - 1 ? '2px 2px 0 0' : 0,
                }} />
              ))}
              <div style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: `${(total / max) * 100}%`,
                textAlign: 'center',
                fontSize: 8,
                fontWeight: 800,
                color: '#0f172a',
                transform: 'translateY(-10px)',
              }}>
                {total}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        position: 'absolute', left: 28, right: 8,
        top: `${18 + (1 - cap / max) * 78}%`,
        borderTop: '2px solid #0f172a',
      }} />
      {overLine && (
        <div style={{
          position: 'absolute', right: 10, top: 10, background: '#fff1f2',
          color: '#be123c', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, border: '1px solid #fecdd3',
        }}>
          Over the line
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 8px 2px 28px', fontSize: 8, color: '#94a3b8' }}>
        <span>Sep 6</span><span>Oct 4</span><span>Nov 1</span><span>Nov 29</span><span>Dec 27</span>
      </div>
    </div>
  );
}

export function FrameFabHero({ accent = ACCENT, title = 'Is the shop too full?' }) {
  return (
    <WindowsChrome title={title} accent={accent}>
      <FabChrome>
        <div style={{ display: 'flex', height: '100%', gap: 6 }}>
          <MiniLegend />
          <MiniChart overLine />
        </div>
      </FabChrome>
    </WindowsChrome>
  );
}

export function FrameFabReload({ accent = ACCENT, title = 'When the hours move' }) {
  return (
    <WindowsChrome title={title} accent={accent}>
      <FabChrome highlight="reload">
        <div style={{
          height: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10,
        }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Pipe Fabrication · Data Dump</div>
          {['Atura 26LG0126.xlsx', 'ACI 26FL1006.xlsx', 'Airgas 25US1080.xlsx'].map((f) => (
            <div key={f} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: 11,
            }}>
              <span style={{ color: '#334155' }}>{f}</span>
              <span style={{ color: ACCENT, fontWeight: 700, fontSize: 10 }}>Sheet1 · Remaining Units</span>
            </div>
          ))}
          <div style={{ fontSize: 10, color: '#64748b' }}>
            Import these workbooks. The chart reloads when the upload finishes.
          </div>
        </div>
      </FabChrome>
    </WindowsChrome>
  );
}

export function FrameFabPlanning({ accent = ACCENT, title = 'Crew and the shop week' }) {
  const rows = [
    ['East', 5], ['West', 5], ['FL', 10], ['Tank', 5],
  ];
  return (
    <WindowsChrome title={title} accent={accent}>
      <FabChrome highlight="planning">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, height: '100%' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: ACCENT, letterSpacing: 0.3, marginBottom: 8 }}>SHOP MANPOWER</div>
            {rows.map(([shop, men]) => (
              <div key={shop} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#334155' }}>{shop}</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{men}</span>
              </div>
            ))}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: ACCENT, letterSpacing: 0.3, marginBottom: 8 }}>COMPANY DEFAULTS</div>
            <div style={{ fontSize: 11, color: '#334155', lineHeight: 1.7 }}>
              Hours / week fallback <strong>32</strong><br />
              Default absenteeism <strong>8 percent</strong><br />
              Named-shop calendar overrides All shops
            </div>
            <div style={{ marginTop: 10, fontSize: 10, color: '#64748b', lineHeight: 1.45 }}>
              Bars stay planned hours. Only the dark line and man-weeks move when you Save planning.
            </div>
          </div>
        </div>
      </FabChrome>
    </WindowsChrome>
  );
}

export function FrameFabFilters({ accent = ACCENT, title = 'Keep the page quiet' }) {
  return (
    <WindowsChrome title={title} accent={accent}>
      <FabChrome highlight="filters">
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, height: '100%', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <Chip>Weekly</Chip>
            <Chip>Hours</Chip>
            <Chip on>Man-weeks</Chip>
            <Chip>Capacity line</Chip>
            <Chip on>Bar totals</Chip>
            <Chip>Men on bars</Chip>
            <Chip on>Hide cancelled</Chip>
            <Chip>Shop</Chip>
            <Chip>Status</Chip>
          </div>
          <div style={{ display: 'flex', height: 'calc(100% - 36px)', gap: 6 }}>
            <MiniLegend spotlight="Atura 26LG0126" />
            <MiniChart />
          </div>
        </div>
      </FabChrome>
    </WindowsChrome>
  );
}

export function FrameFabPortfolio({ accent = ACCENT, title = 'Many jobs, one curve' }) {
  return (
    <WindowsChrome title={title} accent={accent}>
      <FabChrome highlight="filters">
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, height: '100%', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <Chip on>Program · Goodyear</Chip>
            <Chip>Portfolio · Canada</Chip>
            <Chip>Define programs / portfolios</Chip>
            <Chip>Resource discipline</Chip>
            <Chip>Clear all</Chip>
          </div>
          <div style={{ display: 'flex', height: 'calc(100% - 36px)', gap: 6 }}>
            <MiniLegend />
            <MiniChart />
          </div>
        </div>
      </FabChrome>
    </WindowsChrome>
  );
}

export function FrameFabNext({ accent = ACCENT, title = 'What comes next' }) {
  return (
    <WindowsChrome title={title} accent={accent}>
      <FabChrome highlight="source">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, height: '100%' }}>
          <div style={{ background: '#fff', border: `2px solid ${ACCENT}`, borderRadius: 8, padding: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 11, color: ACCENT, marginBottom: 6 }}>Primavera P6 · default</div>
            <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.45 }}>
              Latest dated weekly. Every remaining resource. Remaining split evenly.
            </div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #99f6e4', borderRadius: 8, padding: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 11, color: '#0f766e', marginBottom: 6 }}>Excel dumps</div>
            <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.45 }}>
              Optional shop files — one workbook you edit and upload back.
            </div>
          </div>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 11, color: '#92400e', marginBottom: 6 }}>Cost breakdown</div>
            <div style={{ fontSize: 10, color: '#92400e', lineHeight: 1.45 }}>
              Coming soon. A bid change updates the load.
            </div>
          </div>
        </div>
      </FabChrome>
    </WindowsChrome>
  );
}
